import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { Icon } from '@frontend/icons';
import {
  GroupParticipant,
  getGroupParticipantDisplayName,
  getGroupParticipantId,
} from '@frontend/thread-header-context';
import { NakedButton, PopoverMenu, PopoverMenuItem, Text, usePopoverMenu } from '@frontend/design-system';
import { containerStyles, buttonStyles, selectedTextStyles } from './template-recipient-field.styles';

interface TemplateRecipientFieldProps {
  person?: Person;
  groupParticipants?: GroupParticipant[];
  selectedParticipantPersonId?: string;
  onSelectParticipant: (personId: string) => void;
}

// Focuses templates on a single group participant, mirroring the 1:1 "focus contact" flow for phone numbers with multiple patients.
export const TemplateRecipientField = ({
  person,
  groupParticipants,
  selectedParticipantPersonId,
  onSelectParticipant,
}: TemplateRecipientFieldProps) => {
  const { getTriggerProps, getMenuProps, getItemProps, isOpen, open, close } = usePopoverMenu({
    placement: 'bottom-start',
  });

  // Group participants flow
  if (groupParticipants?.length) {
    const selectedParticipant =
      groupParticipants.find((p) => getGroupParticipantId(p) === selectedParticipantPersonId) ?? groupParticipants[0];

    const { ref: triggerRef, ...triggerProps } = getTriggerProps();

    return (
      <div css={containerStyles}>
        <Text size='small'>To:</Text>
        <NakedButton
          ref={triggerRef}
          {...triggerProps}
          onClick={isOpen ? close : open}
          css={buttonStyles}
          trackingId='inbox-templates-recipient-selector'
        >
          <Text weight='bold' size='small' css={selectedTextStyles}>
            {getGroupParticipantDisplayName(selectedParticipant as GroupParticipant)}
          </Text>
          <Icon name='caret-down-tiny' size={8} />
        </NakedButton>

        <PopoverMenu {...getMenuProps()}>
          {groupParticipants.map((participant, index) => {
            const participantId = getGroupParticipantId(participant); // non-empty even for phone-only participants

            return (
              <PopoverMenuItem
                key={participantId}
                active={participantId === selectedParticipantPersonId}
                {...getItemProps({ index, onClick: () => onSelectParticipant(participantId) })}
              >
                {getGroupParticipantDisplayName(participant)}
              </PopoverMenuItem>
            );
          })}
        </PopoverMenu>
      </div>
    );
  }

  // Single person flow
  if (!person) return null;

  return (
    <div css={containerStyles}>
      <Text size='small'>To:</Text>
      <Text weight='bold' size='small'>
        {person.firstName} {person.lastName}
      </Text>
    </div>
  );
};
